<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import {
    cn,
    Modal,
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarHeader,
    SidebarInset,
    SidebarProvider
  } from '@gat-ship/vue-components'
  import { IconClose, IconMaximize, IconMinimize } from '@gat-ship/icons'
  import {
    definePermissionsView,
    providePermissionsView
  } from '../../composables/usePermissionsView'
  import { useAccessRoleDetailProvider } from '../providers/access-role-detail-context'
  import AccessRoleSwitcher from '../AccessRoleSwitcher.vue'
  import AccessRolePermissionsSectionMenu from '../AccessRolePermissionsSectionMenu.vue'
  import AccessRoleSidebarUsers from '../sidebars/AccessRoleSidebarUsers.vue'
  import AccessRolePermissionsContent from '../AccessRolePermissionsContent.vue'

  const props = defineProps<{
    accessRoleId?: string
  }>()

  const open = defineModel<boolean>('open', { required: true })

  const emit = defineEmits<{
    (e: 'select', roleId: string): void
  }>()

  const { dataSource } = useAccessRoleDetailProvider()
  const accessRole = computed(() => dataSource.accessRole.value)
  const headerTitle = computed(() => accessRole.value?.name ?? 'Access Role')

  // The permissions grid + scroll-to menu read the search/loading UI state from
  // this context; provide it here so both the sidebar and the content share it.
  const permissionsView = definePermissionsView()
  providePermissionsView(permissionsView)

  // The detail provider only fetches on mount, so the modal drives the
  // id-change fetch (and on deep-link restore) — mirroring the routed
  // AccessRolePermissionsView watch.
  watch(
    () => props.accessRoleId,
    async (value) => {
      if (value) {
        permissionsView.setLoading(true)
        await dataSource.fetchAccessRole(value, { subscribeToEvents: false })
        permissionsView.setLoading(false)
      }
    },
    { immediate: true }
  )

  // Viewport-filling mode, toggled from the header next to the close button.
  // Kept across open/close within the session, not persisted. Mirrors
  // AgencyDetailModal.
  const isFullScreen = ref(false)
</script>

<template>
  <Modal
    v-model:open="open"
    type="dialog"
    :max-width="1000"
    full-screen-mobile
    hide-close-button
    :full-screen="isFullScreen"
  >
    <SidebarProvider
      :class="
        cn(
          'h-full min-h-0 w-full overflow-hidden',
          isFullScreen ? 'md:h-full' : 'md:h-[min(800px,85vh)] md:rounded-lg'
        )
      "
    >
      <ResizablePanelGroup
        direction="horizontal"
        auto-save-id="access-role-modal-sidebar"
      >
        <ResizablePanel
          class="hidden md:block"
          :default-size="32"
          :min-size="28"
          :max-size="48"
        >
          <Sidebar
            collapsible="none"
            class="bg-muted/50 hidden h-full w-full p-3 dark:bg-neutral-800/50 md:flex"
          >
            <SidebarContent class="gap-3 overflow-y-auto">
              <SidebarGroup class="p-0">
                <SidebarGroupContent>
                  <SidebarHeader class="px-1 py-1 text-sm font-medium">
                    Access Role
                  </SidebarHeader>
                  <AccessRoleSwitcher
                    :access-role-id="accessRoleId ?? ''"
                    @select="emit('select', $event)"
                  />
                </SidebarGroupContent>
              </SidebarGroup>

              <SidebarGroup v-if="accessRoleId" class="p-0">
                <SidebarGroupContent>
                  <AccessRolePermissionsSectionMenu />
                </SidebarGroupContent>
              </SidebarGroup>

              <div v-if="accessRoleId" class="mt-auto px-1">
                <AccessRoleSidebarUsers />
              </div>
            </SidebarContent>
          </Sidebar>
        </ResizablePanel>
        <ResizableHandle class="hidden md:flex" />
        <ResizablePanel class="flex flex-col" :default-size="68">
          <SidebarInset class="overflow-hidden">
            <div class="relative z-10 shrink-0">
              <div class="bg-background flex items-center px-3 py-1.5">
                <div class="min-w-0 flex-1">
                  <span
                    class="text-foreground truncate pl-1 text-sm font-semibold md:hidden"
                  >
                    {{ headerTitle }}
                  </span>
                </div>
                <button
                  type="button"
                  class="text-muted-foreground hover:text-foreground hover:bg-accent hidden shrink-0 cursor-pointer rounded-md p-1.5 transition-colors md:block"
                  @click="isFullScreen = !isFullScreen"
                >
                  <IconMinimize v-if="isFullScreen" class="size-4" />
                  <IconMaximize v-else class="size-4" />
                  <span class="sr-only">
                    {{ isFullScreen ? 'Exit full screen' : 'Full screen' }}
                  </span>
                </button>
                <button
                  type="button"
                  class="text-muted-foreground hover:text-foreground hover:bg-accent shrink-0 cursor-pointer rounded-md p-1.5 transition-colors"
                  @click="open = false"
                >
                  <IconClose class="size-4" />
                  <span class="sr-only">Close</span>
                </button>
              </div>
              <div
                class="bg-background pointer-events-none h-3"
                style="
                  mask-image: linear-gradient(to bottom, black, transparent);
                "
              />
            </div>
            <div class="-mt-3 flex flex-1 flex-col gap-6 overflow-y-auto p-5">
              <AccessRolePermissionsContent v-if="accessRoleId" />
              <div
                v-else
                class="text-muted-foreground flex flex-1 items-center justify-center p-8 text-center text-sm"
              >
                Select an access role to view its permissions.
              </div>
            </div>
          </SidebarInset>
        </ResizablePanel>
      </ResizablePanelGroup>
    </SidebarProvider>
  </Modal>
</template>
